import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { badgesQuery } from '@/entities/badge'
import { NotificationRow, notificationsQuery } from '@/entities/notification'
import { roadmapEntry } from '@/shared/config'
import { pluralizeRu } from '@/shared/lib/format'
import { IconButton, Popover, RoadmapBadge, Skeleton, WidgetError } from '@/shared/ui'
import s from './NotificationsBell.module.css'

/**
 * Колокол уведомлений (prototype/Prototype.dc.html:175-205): точка — из дешёвых `badgesQuery`
 * (та же точка, что видна на КАЖДОМ экране), лента — лениво, только пока поповер открыт.
 *
 * Открытие → «отметить все прочитанными» (контракт `markNotificationsRead` без тела: «именно
 * это должно происходить, когда пользователь открывает поповер и видит их целиком»). После
 * мутации инвалидируем ТОЛЬКО `badges` (гасит точку на колоколе) — координатор (Task 16,
 * поправка 2) прямо запретил инвалидировать саму ленту, пока поповер открыт: иначе «N новых»
 * и подсветка непрочитанных исчезли бы прямо под курсором у читающего человека. Лента обновится
 * сама при следующем открытии — за это отвечает `staleTime: 0` в `notificationsQuery`.
 *
 * ⚠️ ЭТО ОПИСАНИЕ ЦЕЛЕВОГО ПОВЕДЕНИЯ, А НЕ ТЕКУЩЕГО. Сервиса уведомлений не существует: лента
 * приходит пустой, `markNotificationsRead` отвечает 501, точка не загорается. Пока строка
 * `header.notifications` стоит в ROADMAP, вызов мутации снят (см. ниже), а поповер вместо пустоты
 * объясняет, что сервис ещё не запущен.
 *
 * «Открыть центр уведомлений» из макета (строка 202) не переносим: адреса такого экрана в
 * роутере нет (в 18 задачах фазы 1 отдельного экрана уведомлений нет), а рисовать ссылку в
 * никуда — тот же случай, что и с кнопкой в `pages/no-companies/ui/NoCompaniesPage.tsx`:
 * настоящего адреса нет, значит нет и кнопки.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false)

  const { data: badges } = useQuery(badgesQuery())
  const {
    data: feed,
    error,
    refetch,
  } = useQuery({ ...notificationsQuery(), enabled: open })

  /*
   * ⚠️ МУТАЦИИ `markNotificationsRead` НА ОТКРЫТИЕ ПОПОВЕРА ЗДЕСЬ НЕТ, пока строка
   * `header.notifications` стоит в ROADMAP. Сервиса уведомлений нет, операция отвечает 501, то есть
   * каждое открытие колокольчика уходило бы заведомо отказным запросом ради того, чтобы погасить
   * точку, которая и так не зажигается. Вернуть надо ровно её: `useMutation` с
   * `markNotificationsReadMutation()` и инвалидацией `badgesQuery` в `onSuccess`, а вызов —
   * в `onOpenChange` под `if (next)`.
   */

  return (
    <Popover
      align="end"
      width={342}
      open={open}
      onOpenChange={setOpen}
      trigger={
        // 17/1.8 — prototype/Prototype.dc.html:177.
        <IconButton
          icon="bell"
          title="Уведомления"
          size={17}
          strokeWidth={1.8}
          dot={badges?.notificationsUnread}
        />
      }
    >
      <div className={s.panel}>
        <div className={s.header}>
          <span className={s.title}>Уведомления</span>
          <RoadmapBadge roadmapKey="header.notifications" className={s.soon} />
          {feed && feed.unreadCount > 0 && (
            <span className={s.badge}>
              {feed.unreadCount} {pluralizeRu(feed.unreadCount, ['новое', 'новых', 'новых'])}
            </span>
          )}
        </div>

        {/* Данные — первыми, ошибка — только если показать нечего. Упавший фоновый перезапрос
            оставляет `data` целой и одновременно выставляет `error`; подробности — в TopBar. */}
        {!feed ? (
          error ? (
            <div className={s.errorWrap}>
              <WidgetError error={error} retry={() => void refetch()} />
            </div>
          ) : (
            <div className={s.skeletons}>
              <Skeleton width="100%" height={54} radius={0} />
              <Skeleton width="100%" height={54} radius={0} />
              <Skeleton width="100%" height={54} radius={0} />
            </div>
          )
        ) : feed.items.length === 0 ? (
          // Не «Уведомлений нет»: пустая лента без объяснения читается как «всё разобрано», то есть
          // ровно наоборот. Пока сервиса нет, поповер обязан сказать это словами (0.5).
          <div className={s.empty}>{roadmapEntry('header.notifications').note}</div>
        ) : (
          feed.items.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onNavigate={() => setOpen(false)}
            />
          ))
        )}
      </div>
    </Popover>
  )
}
